import React, { useState } from 'react';
import { CharacterRole, Fleet, GameState, StarSystem } from '../../types';

type Props = {
  fleet: Fleet;
  gameState: GameState;
  myPlayerId: string | null;
  onMoveFleet: (fleetId: number, destinationId: number) => void;
  onCombineFleets?: (fleetIds: number[]) => void;
  onDivideFleet?: (fleetId: number, count: number) => void;
  onAssignCharacterRole: (role: CharacterRole, targetId: number) => void;
};

export default function FleetPanel({ fleet, gameState, myPlayerId, onMoveFleet, onCombineFleets, onDivideFleet, onAssignCharacterRole }: Props) {
  const [divideCount, setDivideCount] = useState<number>(2);
  const me = myPlayerId ? gameState.players.find(p => p.id === myPlayerId) : null;

  const currentSystem = gameState.systems.find(s => s.id === fleet.location);
  const destinations = currentSystem ? currentSystem.adjacentSystems.map(id => gameState.systems.find(s => s.id === id)).filter(Boolean) : [];
  const canBombardPopulation = !!currentSystem && currentSystem.owner !== myPlayerId && !fleet.inTransit && !fleet.inCombat;

  return (
    <div className="console-body">
      <div className="console-header">
        <div className="title">Fleet F{fleet.id}</div>
        <div className="subtitle">{fleet.inTransit ? 'In Transit' : fleet.inCombat ? 'In Combat' : 'Ready'}</div>
      </div>

      <div className="card-grid">
        <div className="card"><div className="label">Commander</div><div className="value">{gameState.players.find(p => p.id === fleet.owner)?.name || (fleet.owner as any)}</div></div>
        <div className="card"><div className="label">Strength</div><div className="value">{Math.round(fleet.power)}/{Math.round(fleet.maxPower)}</div></div>
        <div className="card"><div className="label">Ships</div><div className="value">{fleet.ships?.length ?? 0}</div></div>
        <div className="card"><div className="label">Location</div><div className="value">{currentSystem?.name ?? '—'}</div></div>
      </div>

      {/* Composition summary */}
      <div className="card">
        <div className="label">Composition</div>
        {(() => {
          const counts: Record<string, number> = { frigate: 0, destroyer: 0, cruiser: 0, capital: 0 };
          (fleet.ships || []).forEach(s => { counts[s.class] = (counts[s.class] || 0) + 1; });
          const pills = [
            { k: 'frigate', label: 'Frigate' },
            { k: 'destroyer', label: 'Destroyer' },
            { k: 'cruiser', label: 'Cruiser' },
            { k: 'capital', label: 'Capital' },
          ];
          return (
            <div className="row wrap" style={{ gap: 8 }}>
              {pills.map(p => (
                <div key={p.k} className="pill" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' }}>
                  {p.label}: <strong style={{ marginLeft: 4 }}>{counts[p.k] || 0}</strong>
                </div>
              ))}
            </div>
          );
        })()}
      </div>

      {/* Ship roster with health */}
      <div className="card">
        <div className="label">Ships</div>
        <div className="stack" style={{ maxHeight: 220, overflowY: 'auto' }}>
          {(fleet.ships || []).slice().sort((a, b) => {
            const order: Record<string, number> = { frigate: 0, destroyer: 1, cruiser: 2, capital: 3 };
            const oa = order[a.class] ?? 0; const ob = order[b.class] ?? 0;
            if (oa !== ob) return oa - ob;
            return a.id - b.id;
          }).map((s) => {
            const pct = s.maxHp > 0 ? Math.max(0, Math.min(100, (s.hp / s.maxHp) * 100)) : 0;
            const color = pct > 60 ? '#00c853' : pct > 30 ? '#ffb300' : '#ff5252';
            const icon = s.class === 'frigate' ? '⚔️' : s.class === 'destroyer' ? '🛡️' : s.class === 'cruiser' ? '🛰️' : '👑';
            const label = s.class.charAt(0).toUpperCase() + s.class.slice(1);
            return (
              <div key={`${s.class}-${s.id}`} className="row between" style={{ alignItems: 'center', gap: 8 }}>
                <div style={{ minWidth: 110, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span aria-hidden>{icon}</span>
                  <span style={{ opacity: 0.9 }}>{label}</span>
                  <span style={{ opacity: 0.7, fontSize: 12 }}>#{s.id}</span>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ height: 8, background: '#333', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: color }} />
                  </div>
                </div>
                <div style={{ minWidth: 70, textAlign: 'right', opacity: 0.9 }}>{Math.round(s.hp)}/{Math.round(s.maxHp)}</div>
              </div>
            );
          })}
          {(fleet.ships || []).length === 0 && (
            <div style={{ opacity: 0.75 }}>No ships</div>
          )}
        </div>
      </div>

      {fleet.owner === myPlayerId && !fleet.inTransit && (
        <div className="card">
          <div className="label">{fleet.inCombat ? 'Retreat' : 'Movement'}</div>
          <div className="stack">
            {destinations.map((d) => d && (
              <button key={d.id} className={`action destination ${d.owner === myPlayerId ? 'friendly' : d.owner === 'NPC' ? 'neutral' : 'enemy'}`} onClick={() => onMoveFleet(fleet.id, d.id)}>
                {d.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {fleet.owner === myPlayerId && !fleet.inTransit && !fleet.inCombat && (
        <div className="card">
          <div className="label">Reorganization</div>
          <div className="row gap">
            <input type="number" min={2} value={divideCount} onChange={(e) => setDivideCount(Math.max(2, Number(e.target.value) || 2))} />
            <button className="action secondary" onClick={() => onDivideFleet && onDivideFleet(fleet.id, divideCount)}>Divide into {divideCount}</button>
          </div>
        </div>
      )}

      {fleet.owner === myPlayerId && (
        <div className="card">
          <div className="label">Operations</div>
          <div className="row between">
            <div>Bombard Population</div>
            <button
              className={`action ${fleet.isBombardingPopulation ? 'danger' : 'secondary'}`}
              disabled={!canBombardPopulation}
              title={canBombardPopulation ? 'Toggle bombardment of planetary population' : 'Must be at enemy or NPC system and not in combat'}
              onClick={() => {
                // fire socket event directly from App through global
                (window as any).socket?.emit?.('toggleBombardPopulation', { fleetId: fleet.id, enable: !fleet.isBombardingPopulation });
              }}
            >{fleet.isBombardingPopulation ? 'Stop' : 'Start'}</button>
          </div>
        </div>
      )}

      {me && (
        <div className="card">
          <div className="label">Admiralty</div>
          <div className="row between">
            <div><strong>{me.character.name}</strong> — {CharacterRole[me.character.role]}
              {me.character.role === CharacterRole.Admiral && me.character.assignment === fleet.id && (
                <span className="pill good" title="Admiral bonus"> +{Math.round((me.character.military ?? 0) * 5)}%</span>
              )}
            </div>
            <button className="action secondary" disabled={me.character.role === CharacterRole.Governor} title={me.character.role === CharacterRole.Governor ? 'Currently serving as Governor' : 'Assign as Admiral'} onClick={() => onAssignCharacterRole(CharacterRole.Admiral, fleet.id)}>Appoint Admiral</button>
          </div>
        </div>
      )}
    </div>
  );
}


